import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";

const HERO_IMG = "/hero-sundae.webp";

const LINES = [
  { text: "An Iconic", cls: "italic font-medium text-teal" },
  { text: "Seaside", cls: "font-black text-teal" },
  { text: "Tradition", cls: "font-black text-transparent bg-clip-text bg-gradient-to-r from-gold via-[#d9b878] to-gold" },
  { text: "Since 1907", cls: "italic font-medium text-blush drop-shadow-[0_1px_0_rgba(91,42,134,0.35)]" },
];

const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });

  const onMouseMove = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-20">
      <div className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full bg-mint/60 blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-0 -left-40 w-[30rem] h-[30rem] rounded-full bg-blush/50 blur-3xl" aria-hidden="true" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xs md:text-sm uppercase tracking-[0.4em] text-teal/70 font-medium mb-8 flex items-center gap-3"
          >
            <span className="w-10 h-px bg-gold inline-block" />
            Morelli&rsquo;s &middot; Broadstairs &middot; Kent
          </motion.p>

          <h1 className="font-display tracking-tight leading-[0.92] text-[13.5vw] sm:text-7xl lg:text-[5.6rem]">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.1, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-8 max-w-md text-base md:text-lg text-teal/75 leading-relaxed"
          >
            Enjoy award-winning ice cream, handcrafted desserts, and unforgettable
            family moments in the heart of Broadstairs.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a href="#menu" data-testid="hero-cta-menu"
              className="rounded-full bg-teal text-cream px-8 py-4 text-sm uppercase tracking-[0.2em] font-medium hover:bg-gold transition-colors duration-300 active:scale-95 shadow-lg shadow-teal/20">
              View Our Menu
            </a>
            <a href="#visit" data-testid="hero-cta-visit"
              className="rounded-full border-2 border-teal/80 text-teal px-8 py-4 text-sm uppercase tracking-[0.2em] font-medium hover:border-gold hover:text-gold transition-colors duration-300 active:scale-95">
              Visit Us Today
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative" ref={ref} onMouseMove={onMouseMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <svg viewBox="0 0 200 200" aria-hidden="true"
              className="absolute -inset-10 m-auto w-[120%] h-[120%] animate-spin-slow opacity-40">
              {Array.from({ length: 36 }).map((_, i) => (
                <line key={i} x1="100" y1="100" x2="100" y2="6" stroke="#C5A059" strokeWidth="1" transform={`rotate(${i * 10} 100 100)`} />
              ))}
            </svg>

            <motion.div style={reduce ? {} : { rotateX, rotateY, transformPerspective: 1000 }}
              className="relative rounded-t-full rounded-b-[2.5rem] overflow-hidden border-[6px] border-gold/70 shadow-2xl shadow-teal/25 aspect-[4/5] max-w-md mx-auto">
              <img src={HERO_IMG} alt="A Morelli's strawberry sundae with whipped cream and a wafer" data-testid="hero-image"
                className="w-full h-full object-cover" loading="eager" fetchpriority="high" />
              <div className="absolute inset-0 bg-gradient-to-t from-teal/30 via-transparent to-transparent" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.35, type: "spring" }}
              className="absolute -top-6 -right-2 md:-right-6 w-24 h-24 md:w-28 md:h-28"
            >
              <svg viewBox="0 0 100 100" className="w-full h-full animate-spin-slow">
                <defs><path id="circlePath" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
                <circle cx="50" cy="50" r="50" fill="#5B2A86" />
                <text fill="#FFF8EE" fontSize="10.5" letterSpacing="2.5">
                  <textPath href="#circlePath">EST. 1907 &middot; MORELLI&rsquo;S &middot; BROADSTAIRS &middot;</textPath>
                </text>
                <circle cx="50" cy="50" r="6" fill="#C5A059" />
              </svg>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
